/** Protokoll: Audit, Lagerbewegungen, Sync-Log, Outbox (inkl. Trockenlauf-Requests), Postausgang, Webhook-Inbox. */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OUTBOX_STATUS, OUTBOX_TYPEN } from '@storeos/contracts';
import { audit, bewegungen, outbox, outboxErneut, postausgang, syncLog, webhookInbox } from '../api/protokoll';
import type { MailLogZeile, OutboxZeile } from '../api/typen';
import { BEWEGUNG_TEXT, datumZeit, KORREKTUR_TEXT } from '../components/format';
import { Detailpanel, Filterleiste, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';

const TABS = [
  { k: 'audit', t: 'Audit' }, { k: 'bewegungen', t: 'Lagerbewegungen' }, { k: 'sync', t: 'Sync' },
  { k: 'outbox', t: 'Outbox' }, { k: 'postausgang', t: 'Postausgang' }, { k: 'webhooks', t: 'Webhooks' },
] as const;
type Tab = (typeof TABS)[number]['k'];

const json = (x: unknown) => JSON.stringify(x, null, 2);

export function Protokoll() {
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as Tab) ?? 'audit';
  const setze = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  return (
    <section>
      <Seitenkopf titel="Protokoll" />
      <div className="tabs" role="tablist">
        {TABS.map((x) => <button key={x.k} type="button" role="tab" aria-selected={tab === x.k} className={tab === x.k ? 'aktiv' : ''} onClick={() => setSp({ tab: x.k }, { replace: true })}>{x.t}</button>)}
      </div>
      {tab === 'audit' && <AuditTab q={sp.get('q') ?? ''} setze={setze} />}
      {tab === 'bewegungen' && <BewegungenTab />}
      {tab === 'sync' && <SyncTab />}
      {tab === 'outbox' && <OutboxTab status={sp.get('status') ?? ''} typ={sp.get('typ') ?? ''} setze={setze} />}
      {tab === 'postausgang' && <PostTab />}
      {tab === 'webhooks' && <WebhookTab />}
    </section>
  );
}

function AuditTab({ q, setze }: { q: string; setze: (k: string, v: string) => void }) {
  const [s, setS] = useState(q);
  const r = useQuery({ queryKey: ['audit', q], queryFn: () => audit({ q: q || undefined }) });
  return (
    <>
      <Filterleiste><form className="suche" onSubmit={(e) => { e.preventDefault(); setze('q', s); }}><input type="search" value={s} onChange={(e) => setS(e.target.value)} placeholder="Aktion, ID oder Grund" aria-label="Audit-Suche" /><button className="knopf" type="submit">Suchen</button></form></Filterleiste>
      <Zustand laden={r.isLoading} fehler={r.error}>
        <Tabelle testid="audit-tabelle" zeilen={r.data?.eintraege ?? []} schluessel={(a) => a.id}
          spalten={[
            { kopf: 'Zeit', zelle: (a) => datumZeit(a.created_at) },
            { kopf: 'Wer', zelle: (a) => a.benutzer ?? 'System' },
            { kopf: 'Aktion', zelle: (a) => a.aktion, klasse: 'mono' },
            { kopf: 'Objekt', zelle: (a) => <span className="mono klein">{a.entitaet} {a.entitaet_id.slice(0, 8)}</span> },
            { kopf: 'Vorher → Nachher', zelle: (a) => <span className="mono klein kuerzen">{a.vorher ? JSON.stringify(a.vorher) : '–'} → {a.nachher ? JSON.stringify(a.nachher) : '–'}</span> },
            { kopf: 'Grund', zelle: (a) => a.grund ?? '', klasse: 'umbruch' },
          ]} />
      </Zustand>
    </>
  );
}

function BewegungenTab() {
  const r = useQuery({ queryKey: ['bewegungen'], queryFn: () => bewegungen() });
  return (
    <Zustand laden={r.isLoading} fehler={r.error}>
      <Tabelle zeilen={r.data?.eintraege ?? []} schluessel={(m) => m.id}
        spalten={[
          { kopf: 'Zeit', zelle: (m) => datumZeit(m.created_at) },
          { kopf: 'Grund', zelle: (m) => BEWEGUNG_TEXT[m.grund] ?? m.grund },
          { kopf: 'Korrektur', zelle: (m) => (m.korrektur_grund ? KORREKTUR_TEXT[m.korrektur_grund] : '') },
          { kopf: 'Artikel', zelle: (m) => <Link to={`/artikel/${m.article_id}`} className="mono">{m.sku}</Link> },
          { kopf: 'Platz', zelle: (m) => m.bin_code ?? '–', klasse: 'mono' },
          { kopf: 'Menge', zelle: (m) => <strong className={m.menge < 0 ? 'negativ' : 'positiv'}>{m.menge > 0 ? `+${m.menge}` : m.menge}</strong>, rechts: true },
          { kopf: 'Bezug', zelle: (m) => [m.ref_typ, m.bemerkung].filter(Boolean).join(' · ') },
          { kopf: 'Wer', zelle: (m) => m.benutzer ?? 'System' },
        ]} />
    </Zustand>
  );
}

function SyncTab() {
  const r = useQuery({ queryKey: ['sync-log'], queryFn: () => syncLog() });
  return (
    <Zustand laden={r.isLoading} fehler={r.error}>
      <Tabelle zeilen={r.data?.eintraege ?? []} schluessel={(x) => x.id}
        spalten={[
          { kopf: 'Zeit', zelle: (x) => datumZeit(x.created_at) },
          { kopf: 'Richtung', zelle: (x) => x.richtung },
          { kopf: 'Art', zelle: (x) => x.art },
          { kopf: 'Extern', zelle: (x) => x.external_id ?? '–', klasse: 'mono' },
          { kopf: 'Ergebnis', zelle: (x) => <span className={`badge erg-${x.ergebnis}`}>{x.ergebnis}</span> },
          { kopf: 'Meldung', zelle: (x) => x.meldung, klasse: 'umbruch' },
        ]} />
    </Zustand>
  );
}

function OutboxTab({ status, typ, setze }: { status: string; typ: string; setze: (k: string, v: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const r = useQuery({ queryKey: ['outbox', status, typ], queryFn: () => outbox({ status: status || undefined, typ: typ || undefined }) });
  const [auswahl, setAuswahl] = useState<OutboxZeile | null>(null);
  const typen = Object.values(OUTBOX_TYPEN).flat();
  return (
    <div className={auswahl ? 'mit-panel' : ''}>
      <div className="hauptbereich">
        <Filterleiste>
          <select aria-label="Outbox-Status" value={status} onChange={(e) => setze('status', e.target.value)} data-testid="outbox-status">
            <option value="">Status: alle</option>
            {OUTBOX_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select aria-label="Outbox-Typ" value={typ} onChange={(e) => setze('typ', e.target.value)} data-testid="outbox-typ">
            <option value="">Typ: alle</option>
            {typen.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="hinweis">Trockenlauf = Schalter in .env aus; der Request wird nur protokolliert, nicht gesendet.</span>
        </Filterleiste>
        <Zustand laden={r.isLoading} fehler={r.error}>
          <Tabelle testid="outbox-tabelle" zeilen={r.data?.eintraege ?? []} schluessel={(x) => x.id} aktiv={auswahl?.id} onZeile={setAuswahl}
            spalten={[
              { kopf: 'Zeit', zelle: (x) => datumZeit(x.created_at) },
              { kopf: 'Typ', zelle: (x) => x.typ, klasse: 'mono' },
              { kopf: 'Status', zelle: (x) => <span className={`badge ob-${x.status}`} data-status={x.status}>{x.status}</span> },
              { kopf: 'Versuche', zelle: (x) => x.versuche, rechts: true },
              { kopf: 'Schlüssel', zelle: (x) => <span className="mono klein kuerzen">{x.idempotenz_key}</span> },
              { kopf: 'Fehler', zelle: (x) => <span className="kuerzen">{x.letzter_fehler ?? ''}</span> },
            ]} />
        </Zustand>
      </div>
      {auswahl && (
        <Detailpanel titel={<span className="mono">{auswahl.typ}</span>} onSchliessen={() => setAuswahl(null)}>
          <div className="zeile"><span className={`badge ob-${auswahl.status}`}>{auswahl.status}</span> <span className="hinweis">{datumZeit(auswahl.created_at)} · {auswahl.versuche} Versuche</span></div>
          {auswahl.letzter_fehler && <p className="fehler">{auswahl.letzter_fehler}</p>}
          <h3>{auswahl.status === 'trockenlauf' ? 'Request (Trockenlauf, nicht gesendet)' : 'Ergebnis'}</h3>
          <pre className="json" data-testid="outbox-request">{json(auswahl.ergebnis)}</pre>
          <h3>Payload</h3>
          <pre className="json">{json(auswahl.payload)}</pre>
          {auswahl.status === 'fehlgeschlagen' && <button type="button" className="knopf" onClick={async () => { try { await outboxErneut(auswahl.id); toast('Neu eingeplant'); setAuswahl(null); void qc.invalidateQueries({ queryKey: ['outbox'] }); } catch (e) { toast((e as Error).message, 'fehler'); } }}>Erneut versuchen</button>}
        </Detailpanel>
      )}
    </div>
  );
}

function PostTab() {
  const r = useQuery({ queryKey: ['postausgang'], queryFn: postausgang });
  const [m, setM] = useState<MailLogZeile | null>(null);
  return (
    <div className={m ? 'mit-panel' : ''}>
      <div className="hauptbereich">
        <Zustand laden={r.isLoading} fehler={r.error}>
          <Tabelle testid="post-tabelle" zeilen={r.data?.eintraege ?? []} schluessel={(x) => x.id} aktiv={m?.id} onZeile={setM}
            spalten={[
              { kopf: 'Zeit', zelle: (x) => datumZeit(x.created_at) },
              { kopf: 'An', zelle: (x) => x.an },
              { kopf: 'Betreff', zelle: (x) => x.betreff },
              { kopf: 'Modus', zelle: (x) => <span className={`badge modus-${x.modus}`}>{x.modus === 'mock' ? 'MOCK' : 'LIVE'}</span> },
              { kopf: 'Status', zelle: (x) => x.status },
            ]} />
        </Zustand>
      </div>
      {m && (
        <Detailpanel titel={m.betreff} onSchliessen={() => setM(null)}>
          <p className="hinweis">An {m.an} · {datumZeit(m.created_at)}</p>
          <pre className="mail">{m.text}</pre>
          {m.anhaenge.length > 0 && <ul className="liste-einfach">{m.anhaenge.map((x) => <li key={x.dateiname}>{x.dateiname} <span className="hinweis">{Math.round(x.bytes / 1024)} KB</span></li>)}</ul>}
        </Detailpanel>
      )}
    </div>
  );
}

function WebhookTab() {
  const r = useQuery({ queryKey: ['webhooks'], queryFn: webhookInbox });
  return (
    <Zustand laden={r.isLoading} fehler={r.error}>
      <Tabelle zeilen={r.data?.eintraege ?? []} schluessel={(x) => x.id}
        spalten={[
          { kopf: 'Zeit', zelle: (x) => datumZeit(x.created_at) },
          { kopf: 'Quelle', zelle: (x) => x.quelle },
          { kopf: 'Topic', zelle: (x) => x.topic, klasse: 'mono' },
          { kopf: 'Zustellung', zelle: (x) => x.delivery_id, klasse: 'mono' },
          { kopf: 'Signatur', zelle: (x) => (x.signatur_ok ? 'ok' : <span className="negativ">falsch</span>) },
          { kopf: 'Verarbeitet', zelle: (x) => (x.fehler ? <span className="negativ">{x.fehler}</span> : datumZeit(x.verarbeitet_at)) },
        ]} />
    </Zustand>
  );
}
